import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'logic/moment';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@material-ui/core/Box';
import StatusText from 'components/shared/StatusText';
import CircularProgress from 'components/shared/CircularProgress';
import { Button, Divider, List, ListItem, ListItemText, Paper } from '@material-ui/core';
import PATHS from 'constants/paths';
import BookingListForm from './BookingsListForm';

const formatDate = (dateStr) => moment(dateStr).format('LLLL');

const BookingsList = ({ filters, isLoading, list, onSubmit, currentUser }) => {
  const intl = useIntl();

  return (
    <Paper>
      {!isLoading && (
        <Box p={2}>
          <BookingListForm initialValues={filters} onSubmit={onSubmit} />
          <Divider />
        </Box>
      )}
      {!isLoading && list.length === 0 && (
        <ListItem>
          {!filters.status && !filters.checkInFrom && !filters.checkInTo ? (
            <ListItemText
              secondary={
                <FormattedMessage
                  id="bookings.noBookings"
                  defaultMessage="Here you can see bookings that you have send
                    and revived thought Stugknutens booking service,
                    you don’t have any bookings yet"
                />
              }
            />
          ) : (
            <ListItemText
              secondary={
                <FormattedMessage
                  id="bookings.noBookingsWithFilters"
                  defaultMessage="There are no bookings for selected filters"
                />
              }
            />
          )}
        </ListItem>
      )}
      <List disablePadding>
        {list.map(
          ({
            id,
            check_in,
            check_out,
            conversation_id,
            cottage_id,
            can_leave_review,
            cottage,
            status,
            Host,
            Guest,
          }) => {
            let guestName = Guest.name;

            if (guestName === 'Deleted') {
              guestName = intl.formatMessage({ id: 'Deleted', defaultMessage: 'Deleted' });
            }

            return (
              <React.Fragment key={id}>
                <ListItem>
                  <ListItemText
                    primary={`#${id} ${cottage.title_sv}`}
                    secondary={
                      <>
                        <span style={{ display: 'block' }}>
                          {formatDate(check_in)} <FormattedMessage id="to" defaultMessage="to" />{' '}
                          {formatDate(check_out)}
                        </span>
                        {Host && Host.id === currentUser?.id && (
                          <span>
                            <FormattedMessage id="Guest" defaultMessage="Guest" />: {guestName}
                          </span>
                        )}
                      </>
                    }
                  />
                  <StatusText status={status} />
                </ListItem>
                <ListItem divider>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    component={Link}
                    to={PATHS.conversations.read.replace(':conversationId', conversation_id)}
                    style={{ marginRight: '8px' }}
                  >
                    <FormattedMessage id="Go to conversation" defaultMessage="Go to conversation" />
                  </Button>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    component={Link}
                    to={PATHS.conversations.booking
                      .replace(':conversationId', conversation_id)
                      .replace(':bookingId', id)}
                    style={{ marginRight: '8px' }}
                  >
                    <FormattedMessage id="View booking" defaultMessage="View booking" />
                  </Button>
                  {Guest && Guest.id === currentUser?.id && can_leave_review && (
                    <Button
                      color="primary"
                      size="small"
                      variant="outlined"
                      component={Link}
                      to={PATHS.reviews.add
                        .replace(':cottageId', cottage_id)
                        .replace(':bookingId', id)}
                    >
                      <FormattedMessage id="Add review" defaultMessage="Add review" />
                    </Button>
                  )}
                </ListItem>
              </React.Fragment>
            );
          },
        )}
        {isLoading && <CircularProgress />}
      </List>
    </Paper>
  );
};

BookingsList.propTypes = {
  filters: PropTypes.shape({
    status: PropTypes.string,
    order: PropTypes.string,
    checkInFrom: PropTypes.string,
    checkInTo: PropTypes.string,
  }).isRequired,

  isLoading: PropTypes.bool,
  list: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.number,
  }),

  onSubmit: PropTypes.func.isRequired,
};

export default BookingsList;
